import React, { useState, useEffect } from 'react';
import { X, Shield, Eye, TrendingUp } from 'lucide-react';

const ConsentBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [bannerConfig, setBannerConfig] = useState(null);
  const [consentChoices, setConsentChoices] = useState({
    necessary: true,
    analytics: false,
    marketing: false
  });
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    checkConsentStatus();
  }, []);

  const checkConsentStatus = async () => {
    try {
      // Check if user has already made consent choices
      const token = localStorage.getItem('token');
      if (!token) return;

      const [bannerResponse, statusResponse] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/consent/banner`),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/consent/status`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
      ]);

      if (bannerResponse.ok) {
        const bannerData = await bannerResponse.json();
        setBannerConfig(bannerData);

        if (statusResponse.ok) {
          const statusData = await statusResponse.json();
          const hasConsent = Object.keys(statusData.consent_status || {}).length > 0;
          
          // Show banner if consent is enabled and user hasn't made choices
          setIsVisible(bannerData.enabled && !hasConsent);
          
          if (hasConsent) {
            setConsentChoices({
              necessary: true, // Always true
              analytics: statusData.consent_status.analytics || false,
              marketing: statusData.consent_status.marketing || false
            });
          }
        } else {
          // User not logged in or error - show banner if enabled
          setIsVisible(bannerData.enabled);
        }
      }
    } catch (error) {
      console.error('Error checking consent status:', error);
    }
  };

  const handleConsentChoice = async (category, allowed) => {
    setConsentChoices(prev => ({
      ...prev,
      [category]: allowed
    }));
  };

  const saveConsent = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        // For non-logged in users, save to localStorage
        localStorage.setItem('consent_choices', JSON.stringify(consentChoices));
        setIsVisible(false);
        return;
      }

      // Save each consent choice
      const promises = Object.entries(consentChoices).map(([category, allowed]) =>
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/consent/choice`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            category,
            allowed,
            ip_address: null, // Could be populated from client IP
            region: null // Could be populated from geolocation
          })
        })
      );

      await Promise.all(promises);
      
      // Apply consent choices
      applyConsentChoices(consentChoices);
      
      setIsVisible(false);
    } catch (error) {
      console.error('Error saving consent:', error);
    }
  };

  const acceptAll = async () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true
    };
    
    setConsentChoices(allAccepted);
    
    // Save immediately
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const promises = Object.entries(allAccepted).map(([category, allowed]) =>
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/consent/choice`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ category, allowed })
          })
        );

        await Promise.all(promises);
      } catch (error) {
        console.error('Error saving consent:', error);
      }
    } else {
      localStorage.setItem('consent_choices', JSON.stringify(allAccepted));
    }
    
    applyConsentChoices(allAccepted);
    setIsVisible(false);
  };

  const rejectAll = async () => {
    const allRejected = {
      necessary: true, // Necessary cookies cannot be rejected
      analytics: false,
      marketing: false
    };
    
    setConsentChoices(allRejected);
    
    // Save immediately
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const promises = Object.entries(allRejected).map(([category, allowed]) =>
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/consent/choice`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ category, allowed })
          })
        );

        await Promise.all(promises);
      } catch (error) {
        console.error('Error saving consent:', error);
      }
    } else {
      localStorage.setItem('consent_choices', JSON.stringify(allRejected));
    }
    
    applyConsentChoices(allRejected);
    setIsVisible(false);
  };

  const applyConsentChoices = (choices) => {
    // Apply analytics consent
    if (choices.analytics) {
      // Enable analytics scripts (Google Analytics, etc.)
      console.log('Analytics enabled');
      // window.gtag && window.gtag('consent', 'update', { analytics_storage: 'granted' });
    } else {
      // Disable analytics
      console.log('Analytics disabled');
      // window.gtag && window.gtag('consent', 'update', { analytics_storage: 'denied' });
    }

    // Apply marketing consent
    if (choices.marketing) {
      // Enable marketing scripts (Facebook Pixel, Google Ads, etc.)
      console.log('Marketing enabled');
      // window.gtag && window.gtag('consent', 'update', { ad_storage: 'granted' });
    } else {
      // Disable marketing
      console.log('Marketing disabled');
      // window.gtag && window.gtag('consent', 'update', { ad_storage: 'denied' });
    }
  };

  if (!isVisible || !bannerConfig) {
    return null;
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-lg">
      <div className="max-w-7xl mx-auto p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1 pr-4">
            <div className="flex items-center gap-2 mb-2">
              <Shield className="h-5 w-5 text-blue-600" />
              <h3 className="font-semibold text-gray-900">Cookie Preferences</h3>
            </div>
            
            <p className="text-sm text-gray-600 mb-4">
              We use cookies to enhance your experience, analyze site traffic, and for marketing purposes. 
              You can customize your preferences below or accept all cookies.
            </p>

            {showDetails && (
              <div className="space-y-3 mb-4">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Shield className="h-4 w-4 text-green-600" />
                    <div>
                      <div className="font-medium text-sm">Necessary Cookies</div>
                      <div className="text-xs text-gray-500">Required for basic site functionality</div>
                    </div>
                  </div>
                  <div className="text-sm font-medium text-green-600">Always Active</div>
                </div>

                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <TrendingUp className="h-4 w-4 text-blue-600" />
                    <div>
                      <div className="font-medium text-sm">Analytics Cookies</div>
                      <div className="text-xs text-gray-500">Help us understand how visitors use our site</div>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={consentChoices.analytics}
                      onChange={(e) => handleConsentChoice('analytics', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Eye className="h-4 w-4 text-purple-600" />
                    <div>
                      <div className="font-medium text-sm">Marketing Cookies</div>
                      <div className="text-xs text-gray-500">Used to show relevant ads and measure ad performance</div>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={consentChoices.marketing}
                      onChange={(e) => handleConsentChoice('marketing', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                  </label>
                </div>
              </div>
            )}
          </div>

          <button
            onClick={() => setIsVisible(false)}
            className="text-gray-400 hover:text-gray-600 p-1"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={acceptAll}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-sm"
          >
            Accept All
          </button>
          
          <button
            onClick={rejectAll}
            className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-medium text-sm"
          >
            Reject All
          </button>
          
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium text-sm"
          >
            {showDetails ? 'Hide Details' : 'Customize'}
          </button>
          
          {showDetails && (
            <button
              onClick={saveConsent}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium text-sm"
            >
              Save Preferences
            </button>
          )}
        </div>

        {bannerConfig.privacy_frameworks.includes('GDPR') && (
          <div className="mt-3 text-xs text-gray-500">
            This site is GDPR compliant. You can change your preferences at any time in Settings.
          </div>
        )}
      </div>
    </div>
  );
};

export default ConsentBanner;